import {Spec} from 'immutability-helper';
import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {PERMISSIONS_SHAPE} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as context from 'Context';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {tableProvenanceUuidsForNotePart} from 'notebook/mediocre/util/TableMapUtil';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import NoteEditor, {NoteEditorContainerProps} from 'notebook/notes/core-ui/NoteEditor';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditorPlugin} from 'notebook/notes/NoteEditorTypes';
import NOTE_EDITOR_PLUGINS from 'notebook/notes/plugins/NoteEditorPlugins';
import getExtraUsedNamesFromNotePart from 'notebook/notes/util/getExtraUsedNamesFromNotePart';
import allowTableMappingInDocument from 'notebook/structured-tables/structured-table-mappings/allowTableMappingInDocument';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {Note, NoteLinkedObject, NotePart, noteType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {ProcedureStepConfigEditorContext} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';

import {SectionConfigurationContext} from './NoteContext';

export function _getType(item: NotePart): string {
  if (item.type === ModelTypeConstants.NOTE_LINKED_OBJECT) {
    if (item.item != null) {
      return (item.item as NoteLinkedObject).type;
    } else {
      // item.item is not populated if we've created a placeholder
      // (using makeFilePlaceholderItem).
      return ModelTypeConstants.NOTE_LINKED_FILE;
    }
  } else {
    return item.type;
  }
}

export function _getPluginByType(type: string): NoteEditorPlugin | null {
  for (const plugin of NOTE_EDITOR_PLUGINS) {
    if (plugin.type === type) {
      return plugin;
    }
  }
  return null;
}

export default class NoteEditorContainer extends React.Component<NoteEditorContainerProps> {
  static propTypes = {
    afterEntry: PropTypes.node,
    banner: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    beforeEntry: PropTypes.node,
    disableFormatting: PropTypes.bool,
    disableMaximizedTables: PropTypes.bool,
    editorIcon: PropTypes.element,
    hideTitle: PropTypes.bool,
    locked: PropTypes.bool.isRequired,
    fullScreen: PropTypes.bool.isRequired,
    fullHeight: PropTypes.bool,
    minimalToolbar: PropTypes.bool,
    note: noteType.isRequired,
    parentType: PropTypes.string.isRequired,
    updateNote: PropTypes.func,
    registerObjects: PropTypes.func.isRequired,
    getIdFromCid: PropTypes.func.isRequired,
    cidIdMap: PropTypes.object,

    preview: PropTypes.object,
    displayOptions: PropTypes.object,
    spellCheck: PropTypes.bool.isRequired,
    showTableOfContents: PropTypes.bool.isRequired,
    showPrintMargins: PropTypes.bool.isRequired,
    printLayout: PropTypes.bool.isRequired,
    requestSelection: PropTypes.func,
    dateSlice: PropTypes.object,
    viewer: PropTypes.bool,
    indexSlice: PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
    }),
    collaboratorIds: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    permissions: PERMISSIONS_SHAPE.isRequired,
    showTimestamps: PropTypes.bool,
    isConfig: PropTypes.bool,
    renderCanvas: PropTypes.func,
    renderCanvasTitle: PropTypes.func,
    maximizedTableProvenanceUuid: PropTypes.string,
    focusedTableViewMode: PropTypes.string,
    onLoad: PropTypes.func,
    tablesToInsertOnCreation: PropTypes.arrayOf(PropTypes.object),
    customInsertableTables: PropTypes.arrayOf(PropTypes.string),
    isVirtualizationEnabled: PropTypes.bool,
    isViewingPublishedVersion: PropTypes.bool,
  };

  static defaultProps = {
    fullScreen: true,
  };

  spreadsheetTypes = [
    NoteConstants.TABLE,
    ModelTypeConstants.DATA_TABLE,
    ModelTypeConstants.REGISTRATION_TABLE,
    ModelTypeConstants.REGISTRATION_TABLE_CONFIG,
    ModelTypeConstants.DATA_TABLE_CONFIG,
    ModelTypeConstants.STRUCTURED_TABLE,
    ModelTypeConstants.TRANSFER_TABLE_CONFIG,
    ModelTypeConstants.TRANSFER_TABLE,
  ];

  getPluginByType(type: string): NoteEditorPlugin | null {
    return _getPluginByType(type);
  }

  makeFullTableMap = (props: NoteEditorContainerProps): FullTableMap => {
    const tableMap: FullTableMap = {};
    for (const notePart of this.spreadsheetRoot(props)) {
      const itemType = this.getType(notePart);
      if (itemType != null && this.spreadsheetTypes.includes(itemType)) {
        const {table} = this.getContent(notePart);
        tableMap[table.name] = table;
      }
    }
    return tableMap;
  };

  _getStoreDependenciesForSpreadsheetEval(item: NotePart): StoreDependencyMap {
    const type = this.getType(item);
    const plugin = this.getPluginByType(type);
    if (plugin && plugin.getStoreDependenciesForSpreadsheetEval) {
      return plugin.getStoreDependenciesForSpreadsheetEval.call(this, item);
    }
    return {};
  }

  getAllStoreDependenciesFromSpreadsheetRoot = (spreadsheetRoot: Array<NotePart>): StoreDependencyMap => {
    const storeDependencies: StoreDependencyMap = {};
    for (const item of spreadsheetRoot) {
      const itemType = this.getType(item);
      if (itemType != null && this.spreadsheetTypes.includes(itemType)) {
        const itemStoreDependencies = this._getStoreDependenciesForSpreadsheetEval(item);
        for (const key of Object.keys(itemStoreDependencies)) {
          if (!storeDependencies[key]) {
            storeDependencies[key] = [];
          }
          storeDependencies[key].push(...itemStoreDependencies[key]);
        }
      }
    }
    for (const key of Object.keys(storeDependencies)) {
      // eslint-disable-next-line no-restricted-syntax
      storeDependencies[key] = _.uniq(storeDependencies[key]);
    }
    return storeDependencies;
  };

  selectorsFromProps = (props: NoteEditorContainerProps): Note['selectors'] => props.note.selectors;

  getSpreadsheetEvalCacheKey = (): string => this.props.fileId;

  getItems = (root: Note): Note['noteParts'] => root.noteParts;

  getCid = (item: NotePart): string => item.cid;

  getProvenanceId = (item: NotePart): number | null => item.provenanceId ?? null;

  getRoot = (props?: NoteEditorContainerProps): Note => (props || this.props).note;

  /** Finds the index of the NotePart that contains the table provenance UUID, or -1 if not found. */
  findIndexForTableProvenanceUuid = (root: Note, tableProvenanceUuid: string): number =>
    root.noteParts.findIndex((notePart) =>
      tableProvenanceUuidsForNotePart(notePart).includes(tableProvenanceUuid)
    );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getContent = (item: NotePart): any => {
    const type = this.getType(item);
    const plugin = this.getPluginByType(type);
    return plugin!.getContent.call(this, item);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setContent = (type: string, content: object): any => {
    const plugin = this.getPluginByType(type);
    return plugin!.setContent.call(this, content);
  };

  getSpreadsheetTableName = (item: NotePart): string => {
    const type = this.getType(item);
    const plugin = this.getPluginByType(type);
    assert(
      plugin != null && plugin.getSpreadsheetTableName !== undefined,
      `Plugin for type ${type} does not have getSpreadsheetTableName`
    );
    return plugin.getSpreadsheetTableName.call(this, item);
  };

  setStructuredTableMappings = (tableMappings: Array<StructuredTableMapping>): void => {
    if (allowTableMappingInDocument(this.props.fileId)) {
      // Need to set performTableEvaluation to true to trigger table mapping sanitization and to update
      // the global table mapping state in EntryStore/TemplateStore/DocumentStore's postSanitizeItem.
      this.props.updateNote!(this.props.note, {performTableEvaluation: true}, tableMappings);
    }
  };

  getType = (item: NotePart): string => _getType(item);

  updateRoot = (
    newNote: Note,
    opts: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    } = {}
  ): void => {
    const isEditAllowed = !this.props.locked || context.get('skipBumpVersionForNonEdit');
    if (!isEditAllowed) {
      if (this.props.lockedFromExternalChange) {
        notifications.error(
          'Cannot update contents because they were updated in another window. ' +
            'Please refresh the page to continue.'
        );
      } else {
        assertAndContinue(false, 'Prevented user from updating locked note');
      }
      return;
    }
    this.props.updateNote!(newNote, opts);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemsAccessor = (op: {[i: number]: Spec<any>}): Spec<any> => ({noteParts: op});

  spreadsheetRoot = (props: NoteEditorContainerProps): Note['noteParts'] => props.note.noteParts;

  getNoteRuntimeSnapshot = (props: NoteEditorContainerProps): Note['noteRuntimeSnapshot'] =>
    props.note.noteRuntimeSnapshot;

  extraUsedTableNames = (): Array<string> =>
    // eslint-disable-next-line no-restricted-syntax
    _.flatten(this.spreadsheetRoot(this.props).map((notePart) => getExtraUsedNamesFromNotePart(notePart)));

  render(): JSX.Element {
    return (
      <EditorPropertiesContext.Consumer>
        {(editorPropertiesContext) => (
          <SectionConfigurationContext.Consumer>
            {(sectionConfiguration) => (
              <StructuredTableInsertionContext.Consumer>
                {(structuredTableInsertionContext) => (
                  <ProcedureStepConfigEditorContext.Consumer>
                    {(procedureStepConfigEditorContext) => (
                      <NoteEditor
                        {...this.props}
                        extraUsedTableNames={this.extraUsedTableNames}
                        getAllStoreDependenciesFromSpreadsheetRoot={
                          this.getAllStoreDependenciesFromSpreadsheetRoot
                        }
                        getContent={this.getContent}
                        getSpreadsheetTableName={this.getSpreadsheetTableName}
                        getItems={this.getItems}
                        getRoot={this.getRoot}
                        getSpreadsheetEvalCacheKey={this.getSpreadsheetEvalCacheKey}
                        getType={this.getType}
                        getCid={this.getCid}
                        findIndexForTableProvenanceUuid={this.findIndexForTableProvenanceUuid}
                        getProvenanceId={this.getProvenanceId}
                        itemsAccessor={this.itemsAccessor}
                        makeFullTableMap={this.makeFullTableMap}
                        selectorsFromProps={this.selectorsFromProps}
                        setContent={this.setContent}
                        setStructuredTableMappings={this.setStructuredTableMappings}
                        spreadsheetRoot={this.spreadsheetRoot}
                        getNoteRuntimeSnapshot={this.getNoteRuntimeSnapshot}
                        spreadsheetTypes={this.spreadsheetTypes}
                        updateRoot={this.updateRoot}
                        sectionConfiguration={sectionConfiguration}
                        structuredTableInsertionContext={structuredTableInsertionContext}
                        procedureStepConfigEditorContext={procedureStepConfigEditorContext}
                        editorPropertiesContext={editorPropertiesContext}
                      />
                    )}
                  </ProcedureStepConfigEditorContext.Consumer>
                )}
              </StructuredTableInsertionContext.Consumer>
            )}
          </SectionConfigurationContext.Consumer>
        )}
      </EditorPropertiesContext.Consumer>
    );
  }
}
